import React, { useEffect, useState } from 'react';

/**
 * A fixed strip along the right edge of the viewport, similar to an editor's
 * "overview ruler": each highlighted or bookmarked paragraph gets a small mark
 * positioned at its proportional location within the full document, so you can
 * see at a glance where your marks are and jump straight to any of them.
 */
export default function OverviewRuler({ nodesRef, highlights, bookmarks, onJump }) {
  const [marks, setMarks] = useState([]);

  useEffect(() => {
    function recompute() {
      const docHeight = document.documentElement.scrollHeight || 1;
      const nextMarks = [];

      const highlightParagraphIds = [...new Set(highlights.map((h) => h.paragraph_id))];
      highlightParagraphIds.forEach((paragraphId) => {
        const node = nodesRef.current[paragraphId];
        if (!node) return;
        const top = node.getBoundingClientRect().top + window.scrollY;
        nextMarks.push({ key: `hl-${paragraphId}`, type: 'highlight', paragraphId, ratio: top / docHeight });
      });

      bookmarks.forEach((b) => {
        const node = nodesRef.current[b.paragraph_id];
        if (!node) return;
        const top = node.getBoundingClientRect().top + window.scrollY;
        nextMarks.push({ key: `bm-${b.id}`, type: 'bookmark', paragraphId: b.paragraph_id, ratio: top / docHeight });
      });

      setMarks(nextMarks);
    }

    // Let layout settle (fonts, image, async content) before measuring.
    const raf = requestAnimationFrame(recompute);

    let resizeTimer;
    const onResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(recompute, 150);
    };
    window.addEventListener('resize', onResize);

    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(resizeTimer);
      window.removeEventListener('resize', onResize);
    };
  }, [nodesRef, highlights, bookmarks]);

  if (marks.length === 0) return null;

  return (
    <div className="study-overview-ruler">
      {marks.map((mark) => (
        <button
          key={mark.key}
          type="button"
          className={`study-overview-mark study-overview-mark--${mark.type}`}
          style={{ top: `${Math.min(Math.max(mark.ratio, 0), 1) * 100}%` }}
          onClick={() => onJump(mark.paragraphId)}
          aria-label={`Jump to ${mark.type === 'bookmark' ? 'bookmark' : 'highlight'}`}
          title={mark.type === 'bookmark' ? 'Jump to bookmark' : 'Jump to highlight'}
        />
      ))}
    </div>
  );
}
